/**
 * Formatting and labels. Nothing in here touches data.
 *
 * WHAT THIS FILE USED TO BE
 * -------------------------
 * A mock dataset: a seeded PRNG keyed on the client id, generating roughly
 * 25,000 rows per client — one per day, per ad, per placement — plus the query
 * functions to aggregate them. Its own header said so, and said what to do
 * about it: "This is the ONLY file that touches data. When ingestion goes live,
 * replace runQuery with a call to your API and delete everything above it."
 *
 * Ingestion went live. The dashboard and the reporting agent moved to the
 * warehouse. Opti-Bid did not, and because this file still exported the
 * generator, Opti-Bid went on inventing its own performance data, analysing
 * it, and sending the findings away to be written up as a strategic briefing.
 * It was the most convincing-looking page in the product and the only one that
 * was not real.
 *
 * So the generator is gone rather than deprecated. A page cannot accidentally
 * render invented figures from a file that cannot produce them, and the next
 * person to need "just some numbers to render against" has to go and get real
 * ones. The analysis that used to run on it is kept as the specification for
 * its server-side replacement — see docs/optibid-checks.js.
 *
 * WHAT REMAINS, AND WHY IT IS SAFE
 * --------------------------------
 * Formatters and display labels. They take a number and return a string. They
 * cannot compute a metric, cannot aggregate, and have no data to do it to.
 *
 * Note that nothing here derives a ratio. CPL, CPA, CTR, CVR and ROAS are
 * computed by BigQuery from the metric registry's definitions, at the grain
 * the question asked for. Recomputing one in the browser is how a dashboard
 * tile and a chat answer end up disagreeing about the same week.
 */

export const METRIC_LABELS = {
  spend: 'Spend', impressions: 'Impressions', reach: 'Reach', clicks: 'Clicks',
  leads: 'Leads', purchases: 'Purchases', revenue: 'Revenue',
  cpl: 'Cost per lead', cpa: 'Cost per purchase', cpc: 'CPC', cpm: 'CPM',
  ctr: 'CTR', cvr: 'Conversion rate', roas: 'ROAS', frequency: 'Frequency',
};

/** Metrics where a fall is an improvement, for colouring a change indicator. */
export const LOWER_IS_BETTER = new Set(['cpl', 'cpa', 'cpc', 'cpm', 'frequency']);

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

export const gbp = (n, dp = 0) =>
  n == null ? '—' : new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: dp, maximumFractionDigits: dp,
  }).format(n);

export const num = (n) =>
  n == null ? '—' : new Intl.NumberFormat('en-GB').format(Math.round(n));

export const pct = (n) => (n == null ? '—' : `${(n * 100).toFixed(2)}%`);

const FORMATTERS = {
  spend: (v) => gbp(v), revenue: (v) => gbp(v),
  cpl: (v) => gbp(v, 2), cpa: (v) => gbp(v, 2), cpc: (v) => gbp(v, 2), cpm: (v) => gbp(v, 2),
  ctr: pct, cvr: pct,
  roas: (v) => (v == null ? '—' : `${v.toFixed(2)}×`),
  frequency: (v) => (v == null ? '—' : `${v.toFixed(2)}×`),
};

export const fmt = (metric, v) => (FORMATTERS[metric] || num)(v);

export const shortDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
